import type { BridgeControllers, BridgeSeat as Seat } from '../bridge/seats.js';
import type { Card, Suit } from '../trick/cards.js';
import {
  type CrazyEightsGame,
  type CrazyEightsPhase,
  activeSuit,
  canDraw,
  crazyEightsLegalPlays,
  topCard,
} from './play.js';

/**
 * Everything one seat may see: its own hand, how many cards the others hold, the top of the discard pile and the suit
 * in force, and how many cards are left to draw. No other hand and no stock order is ever included.
 */
export interface CrazyEightsView {
  readonly viewer: Seat;
  readonly phase: CrazyEightsPhase;
  readonly seats: readonly Seat[];
  readonly controllers: BridgeControllers;
  readonly hand: readonly Card[];
  readonly handSizes: Readonly<Partial<Record<Seat, number>>>;
  readonly top: Card;
  /** The suit a play must match (the declared suit after an eight). */
  readonly activeSuit: Suit;
  readonly stockCount: number;
  readonly discardCount: number;
  readonly turn: Seat;
  readonly canAct: boolean;
  readonly legalPlays: readonly Card[];
  readonly canDraw: boolean;
  /** True when the viewer has just drawn a playable card and may still decline it. */
  readonly canPass: boolean;
  readonly winner: Seat | null;
}

export function crazyEightsViewFor(game: CrazyEightsGame, viewer: Seat): CrazyEightsView {
  const mine = game.phase === 'play' && game.turn === viewer;
  return {
    viewer,
    phase: game.phase,
    seats: game.seats,
    controllers: game.controllers,
    hand: game.hands[viewer] ?? [],
    handSizes: Object.fromEntries(game.seats.map((s) => [s, (game.hands[s] ?? []).length])),
    top: topCard(game),
    activeSuit: activeSuit(game),
    stockCount: game.stock.length,
    discardCount: game.discard.length,
    turn: game.turn,
    canAct: mine,
    legalPlays: mine ? crazyEightsLegalPlays(game) : [],
    canDraw: mine && canDraw(game),
    canPass: mine && game.drew,
    winner: game.winner,
  };
}
